// components/CommandPalette/index.tsx
import { useState, useEffect, useRef, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import type { CommandExecutionResult } from '../../hooks/useCommandExecution';

interface TerminalLine {
  type: 'system' | 'command' | 'success' | 'error';
  text: string;
}

export interface CommandPaletteProps {
  isOpen?: boolean;
  onClose?: () => void;
  /**
   * Runs a command through the shared pipeline (`useCommandExecution` →
   * commandParser/commandExecutor). The palette prints the result into its
   * own terminal, so the caller passes the non-toasting variant. Same
   * contract as StudentCommandBar's `onExecute`.
   */
  onExecute?: (commandString: string) => Promise<CommandExecutionResult>;
}

const CommandPalette = ({
  isOpen,
  onClose,
  onExecute,
}: CommandPaletteProps = {}) => {
  const [command, setCommand] = useState('');
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [terminalOutput, setTerminalOutput] = useState<TerminalLine[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setCommand('');
      setHistoryIndex(-1);
      if (terminalOutput.length === 0) {
        setTerminalOutput([
          { type: 'system', text: 'Academic Planner Command Center v1.0' },
          { type: 'system', text: 'Type "help" for available commands.' },
          { type: 'system', text: '' },
        ]);
      }
    }
  }, [isOpen, terminalOutput.length]);

  useEffect(() => {
    const savedHistory = localStorage.getItem('commandHistory');
    if (savedHistory) {
      try {
        setCommandHistory(JSON.parse(savedHistory));
      } catch {
        // ignore corrupt history
      }
    }
  }, []);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [terminalOutput]);

  const addOutput = (type: TerminalLine['type'], text: string) => {
    setTerminalOutput((prev) => [...prev, { type, text }]);
  };

  const handleExecute = async (cmdToExecute: string = command) => {
    if (!cmdToExecute.trim()) return;

    const cmdUpper = cmdToExecute.toUpperCase().trim();

    addOutput('command', cmdUpper);

    setIsExecuting(true);

    try {
      if (cmdUpper === 'CLEAR') {
        setTerminalOutput([
          { type: 'system', text: 'Academic Planner Command Center v1.0' },
          { type: 'system', text: 'Type "help" for available commands.' },
          { type: 'system', text: '' },
        ]);
        setCommand('');
        setHistoryIndex(-1);
        setIsExecuting(false);
        return;
      }

      if (cmdUpper === 'EXIT' || cmdUpper === 'QUIT') {
        addOutput('system', 'Goodbye!');
        setTimeout(() => {
          if (onClose) onClose();
        }, 500);
        setIsExecuting(false);
        return;
      }

      if (!onExecute) {
        addOutput('error', 'Command execution is unavailable.');
        setCommand('');
        setHistoryIndex(-1);
        setIsExecuting(false);
        return;
      }

      const result = await onExecute(cmdToExecute);

      if (result.success) {
        const lines = result.message.split('\n');
        lines.forEach((line) => addOutput('success', line));

        const newHistory = [
          cmdToExecute,
          ...commandHistory.filter((h) => h !== cmdToExecute),
        ].slice(0, 50);
        setCommandHistory(newHistory);
        localStorage.setItem('commandHistory', JSON.stringify(newHistory));
      } else {
        const lines = result.message.split('\n');
        lines.forEach((line) => addOutput('error', line));
      }

      setCommand('');
      setHistoryIndex(-1);
    } catch (error: unknown) {
      console.error('Command execution error:', error);
      const message = error instanceof Error ? error.message : String(error);
      addOutput('error', `Error: ${message}`);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleExecute();
    } else if (e.key === 'Escape') {
      if (onClose) onClose();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyIndex < commandHistory.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setCommand(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setCommand(commandHistory[newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setCommand('');
      }
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setTerminalOutput([
        { type: 'system', text: 'Academic Planner Command Center v1.0' },
        { type: 'system', text: 'Type "help" for available commands.' },
        { type: 'system', text: '' },
      ]);
    }
  };

  const getOutputColor = (type: TerminalLine['type']): string => {
    switch (type) {
      case 'command':
        return 'text-yellow-400';
      case 'success':
        return 'text-green-400';
      case 'error':
        return 'text-red-400';
      case 'system':
        return 'text-gray-500';
      default:
        return 'text-gray-300';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-2xl max-w-3xl w-full h-[500px] overflow-hidden border border-gray-700 flex flex-col">
        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between border-b border-gray-700">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <button
                onClick={onClose}
                className="w-3 h-3 rounded-full bg-red-500 hover:bg-red-600 transition-colors"
                title="Close"
              />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
            <span className="ml-3 text-gray-400 text-sm font-mono">planner@umd:~</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <div
          ref={outputRef}
          className="flex-1 overflow-y-auto p-4 font-mono text-sm"
          onClick={() => inputRef.current?.focus()}
        >
          {terminalOutput.map((line, index) => (
            <div key={index} className={`${getOutputColor(line.type)} whitespace-pre-wrap`}>
              {line.type === 'command' ? (
                <span>
                  <span className="text-green-500">$</span> {line.text}
                </span>
              ) : (
                line.text || ' '
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-gray-700 bg-gray-800 px-4 py-3">
          <div className="flex items-center gap-2 font-mono text-sm">
            <span className="text-green-500">$</span>
            <input
              ref={inputRef}
              type="text"
              value={command}
              onChange={(e) => setCommand(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent text-gray-100 focus:outline-none placeholder-gray-600"
              placeholder={isExecuting ? 'Executing...' : 'Enter command...'}
              disabled={isExecuting}
              autoComplete="off"
              spellCheck="false"
            />
            {isExecuting && <span className="text-gray-500 animate-pulse">_</span>}
          </div>
          <div className="mt-2 text-xs text-gray-600 flex gap-4">
            <span>
              <kbd className="text-gray-500">Enter</kbd> execute
            </span>
            <span>
              <kbd className="text-gray-500">↑↓</kbd> history
            </span>
            <span>
              <kbd className="text-gray-500">Ctrl+L</kbd> clear
            </span>
            <span>
              <kbd className="text-gray-500">Esc</kbd> close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
